// In-memory purchase orders for demo mode (no Supabase configured). Mirrors
// the RPC shapes so the screens behave the same; rules are simplified.
import type { Page } from "@/components/DataList";
import type {
  ListParams,
  PurchaseOrderDocument,
  PurchaseOrderHeaderInput,
  PurchaseOrderLine,
  PurchaseOrderLineInput,
  PurchaseOrderRow,
  PurchaseOrderStatus,
  SaveResult,
} from "./api";

function mulberry32(seed: number) {
  return () => {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const suppliers = [
  { id: "bp-stalmet", code: "STALMET", name: "Stalmet S.A." },
  { id: "bp-foamtech", code: "FOAMTECH", name: "Foamtech Sp. z o.o." },
  { id: "bp-textilia", code: "TEXTILIA", name: "Textilia" },
  { id: "bp-screwpro", code: "SCREWPRO", name: "ScrewPro" },
];

const catalog = [
  { id: "it-fr", code: "FR-2210", description: "Seat frame steel", price: 46.5 },
  { id: "it-foam", code: "FOAM-45", description: "Seat foam 45 kg/m³", price: 18.2 },
  { id: "it-fab", code: "FAB-GR3", description: "Seat fabric grey", price: 22.9 },
  { id: "it-sc", code: "SC-M6", description: "Screw M6×20", price: 0.12 },
];

const isoDay = (offset: number) => {
  const d = new Date();
  d.setHours(12, 0, 0, 0);
  d.setDate(d.getDate() + offset);
  return d.toISOString().slice(0, 10);
};

function line(no: number, item: (typeof catalog)[number], qty: number, received: number, promised: string): PurchaseOrderLine {
  return {
    id: `l-${no}-${Math.random().toString(36).slice(2, 8)}`,
    line_no: no,
    item_id: item.id,
    item_code: item.code,
    item_description: item.description,
    lot_controlled: false,
    ordered_qty: String(qty),
    uom_id: "u-pcs",
    uom_code: "pcs",
    uom_decimals: 0,
    ordered_base_qty: String(qty),
    received_base_qty: String(received),
    base_uom_code: "pcs",
    base_uom_decimals: 0,
    unit_price: item.price.toFixed(4),
    line_amount: (qty * item.price).toFixed(4),
    promised_date: promised,
    warehouse_id: "wh-main",
    warehouse_code: "MAIN",
    location_id: null,
    location_code: null,
    tolerance_pct: "0",
    status: received >= qty ? "received" : "open",
  };
}

let docs: PurchaseOrderDocument[] = (() => {
  const rand = mulberry32(415);
  const out: PurchaseOrderDocument[] = [];
  for (let n = 120; n > 0; n--) {
    const age = Math.floor((120 - n) * 0.8);
    const r = rand();
    const status: PurchaseOrderStatus =
      age < 3 ? (r < 0.5 ? "draft" : "released") : age < 25 ? (r < 0.5 ? "released" : r < 0.8 ? "partially_received" : "received") : r < 0.1 ? "cancelled" : "closed";
    const sup = suppliers[Math.floor(rand() * suppliers.length)]!;
    const lines: PurchaseOrderLine[] = [];
    const count = 1 + Math.floor(rand() * 3);
    for (let i = 0; i < count; i++) {
      const item = catalog[Math.floor(rand() * catalog.length)]!;
      const qty = item.price < 1 ? 1000 : 10 * (1 + Math.floor(rand() * 20));
      const rec = status === "received" || status === "closed" ? qty : status === "partially_received" ? Math.round(qty / 2) : 0;
      lines.push(line(i + 1, item, qty, rec, isoDay(-age + 7 + Math.floor(rand() * 8))));
    }
    out.push(build(`po-${n}`, `PO26-${String(n).padStart(5, "0")}`, status, sup, isoDay(-age), lines));
  }
  return out;
})();

function build(
  id: string,
  number: string,
  status: PurchaseOrderStatus,
  sup: (typeof suppliers)[number],
  orderDate: string,
  lines: PurchaseOrderLine[],
): PurchaseOrderDocument {
  const open = lines.filter((l) => l.status === "open" && l.promised_date).map((l) => l.promised_date!);
  return {
    id,
    number,
    status,
    company_id: "demo-pl01",
    company_code: "PL01",
    supplier_id: sup.id,
    supplier_code: sup.code,
    supplier_name: sup.name,
    order_date: orderDate,
    currency: "PLN",
    supplier_reference: null,
    note: null,
    warehouse_id: "wh-main",
    warehouse_code: "MAIN",
    next_promised_date: open.sort()[0] ?? null,
    total_amount: lines.reduce((s, l) => s + Number(l.line_amount ?? 0), 0).toFixed(4),
    revision: 1,
    line_count: lines.length,
    released_at: status === "draft" ? null : new Date().toISOString(),
    closed_at: null,
    cancelled_at: null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    created_by_name: "Demo User",
    lines,
  };
}

const toRow = (d: PurchaseOrderDocument): PurchaseOrderRow => ({
  id: d.id,
  number: d.number,
  status: d.status,
  supplier_id: d.supplier_id,
  supplier_code: d.supplier_code,
  supplier_name: d.supplier_name,
  order_date: d.order_date,
  next_promised_date: d.next_promised_date,
  total_amount: d.total_amount,
  currency: d.currency,
  line_count: d.line_count,
});

const delay = <T,>(v: T, ms = 90) => new Promise<T>((r) => setTimeout(() => r(v), ms));
const isOpen = (s: PurchaseOrderStatus) => s === "draft" || s === "released" || s === "partially_received";
let seq = 121;

export const demoPoApi = {
  list(p: ListParams, cursor: string | null): Promise<Page<PurchaseOrderRow>> {
    const q = p.q.trim().toLowerCase();
    const sorted = [...docs].sort((a, b) => (a.order_date === b.order_date ? b.number.localeCompare(a.number) : b.order_date.localeCompare(a.order_date)));
    const filtered = sorted.filter(
      (d) =>
        (p.status === "all" || (p.status === "open" ? isOpen(d.status) : d.status === p.status)) &&
        (!q || d.number.toLowerCase().startsWith(q) || d.supplier_name.toLowerCase().includes(q) || d.supplier_code.toLowerCase().includes(q)),
    );
    const start = cursor ? filtered.findIndex((d) => `${d.order_date}|${d.number}` === cursor) + 1 : 0;
    const page = filtered.slice(start, start + 50);
    const more = start + 50 < filtered.length;
    const last = page[page.length - 1];
    return delay({ rows: page.map(toRow), nextCursor: more && last ? `${last.order_date}|${last.number}` : null });
  },
  get: (id: string) => delay(structuredClone(docs.find((d) => d.id === id) ?? null), 60),
  save(id: string | null, _company: string, supplierId: string, header: PurchaseOrderHeaderInput, input: PurchaseOrderLineInput[]): Promise<SaveResult> {
    const sup = suppliers.find((s) => s.id === supplierId) ?? suppliers[0]!;
    const existing = id ? docs.find((d) => d.id === id) : undefined;
    const lines = input.map((l, i) => {
      const item = catalog.find((c) => c.id === l.item_id) ?? { id: l.item_id, code: l.item_id, description: "", price: 0 };
      const prev = existing?.lines.find((x) => x.id === l.line_id);
      const ln = line(prev?.line_no ?? i + 1, item, Number(l.qty), Number(prev?.received_base_qty ?? 0), l.promised_date ?? "");
      return { ...ln, id: prev?.id ?? ln.id, unit_price: l.unit_price, line_amount: l.unit_price ? (Number(l.qty) * Number(l.unit_price)).toFixed(4) : null, promised_date: l.promised_date };
    });
    if (existing) {
      const updated = { ...build(existing.id, existing.number, existing.status, sup, header.order_date, lines), revision: existing.revision + 1 };
      docs = docs.map((d) => (d.id === existing.id ? updated : d));
      return delay({ id: existing.id, number: existing.number, status: existing.status, revision: updated.revision });
    }
    const doc = build(`po-${seq}`, `PO26-${String(seq).padStart(5, "0")}`, "draft", sup, header.order_date, lines);
    seq++;
    docs = [doc, ...docs];
    return delay({ id: doc.id, number: doc.number, status: "draft", revision: 1 });
  },
  transition(id: string, action: "release" | "cancel" | "close"): Promise<void> {
    const next: Record<typeof action, PurchaseOrderStatus> = { release: "released", cancel: "cancelled", close: "closed" };
    docs = docs.map((d) => (d.id === id ? { ...d, status: next[action] } : d));
    return delay(undefined);
  },
  closeLine(id: string, lineId: string): Promise<void> {
    docs = docs.map((d) => (d.id === id ? { ...d, lines: d.lines.map((l) => (l.id === lineId ? { ...l, status: "closed" as const } : l)) } : d));
    return delay(undefined);
  },
};
